import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged, map } from 'rxjs/operators';

/** Tracks in-flight HTTP requests app-wide (incremented/decremented by
 *  TallysheetHttpInterceptorService) to drive a thin, non-blocking top-of-viewport progress bar.
 *  Deliberately separate from the route-navigation splash (app.component.ts's `loading` field) -
 *  that one is a heavier, full-screen treatment reserved for actual page transitions; this is a
 *  lighter signal for ambient API activity that can happen without any navigation at all. */
@Injectable({ providedIn: 'root' })
export class HttpActivityService {
  private activeCount = 0;
  private readonly activeCount$ = new BehaviorSubject<number>(0);

  // Hard safety net, same pattern AppSplashService already uses for its own timeout - if the
  // counter is ever stuck above zero implausibly long (a bug in some call site's cleanup, or a
  // request that never completes), force it back to zero rather than leave the bar spinning
  // forever with no way for the user to know why.
  private stuckTimer: ReturnType<typeof setTimeout> | null = null;
  private static readonly STUCK_TIMEOUT_MS = 20_000;

  // debounceTime on both directions is deliberate: suppresses the bar entirely for requests that
  // finish faster than the delay (no flicker), and gives a slower request a brief, expected
  // trailing visibility after it completes rather than snapping off instantly - the same feel as
  // NProgress-style bars elsewhere. Not a backdrop, not blocking, so this timing only affects a
  // decorative bar, never anything a user needs to interact with.
  readonly visible$: Observable<boolean> = this.activeCount$.pipe(
    map(count => count > 0),
    distinctUntilChanged(),
    debounceTime(150),
  );

  // Uploads (a request whose body is a FormData) are counted separately, with no stuck timer: a big upload may take
  // minutes. PullToRefreshService reads this so a pull-down can never interrupt an upload. Counted on subscribe and
  // released in finalize(), so success, error and cancel all release it.
  private uploads = 0;
  get uploadsInProgress(): boolean { return this.uploads > 0; }
  startUpload(): void { this.uploads++; }
  endUpload(): void { this.uploads = Math.max(0, this.uploads - 1); }

  start(): void {
    this.activeCount++;
    this.activeCount$.next(this.activeCount);
    if (this.activeCount === 1) {
      this.armStuckTimer();
    }
  }

  /** Safe to call even if start() was somehow never paired (clamped at zero) - a defensive
   *  measure, not an expected path. */
  end(): void {
    this.activeCount = Math.max(0, this.activeCount - 1);
    this.activeCount$.next(this.activeCount);
    if (this.activeCount === 0) {
      this.clearStuckTimer();
    }
  }

  private armStuckTimer(): void {
    this.clearStuckTimer();
    this.stuckTimer = setTimeout(() => {
      if (this.activeCount > 0) {
        console.warn(
          `[HttpActivityService] Active request count stuck above zero for ${HttpActivityService.STUCK_TIMEOUT_MS}ms - resetting.`
        );
        this.activeCount = 0;
        this.activeCount$.next(0);
      }
    }, HttpActivityService.STUCK_TIMEOUT_MS);
  }

  private clearStuckTimer(): void {
    if (this.stuckTimer) {
      clearTimeout(this.stuckTimer);
      this.stuckTimer = null;
    }
  }
}
